import { IPromise } from 'angular';
import { call, put, select } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { L10n } from '../../../../ts/L10n/L10n';
import {
  FlowDefinition,
  GroupDefinition,
  ProjectDefinition,
  PublishedConnectionDefinition,
  ScheduleDefinition,
  WorkbookDefinition,
  CollectionDefinition,
} from '../../../../ts/models/ContentDefinitions';
import { MetricDefinition } from '../../../../metrics/models/MetricDefinition';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import { RenameAction } from '../../../../ts/services/RenameAction';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { RenameContentAction } from '../../../actions/spaces/RenameContentAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { CompositeSessionInfoSelectors } from '../../../selectors/SessionInfoSelectors';

export function* handleRenameContent(action: RenameContentAction) {
  try {
    const ownerSelector = yield call(CompositeSessionInfoSelectors.currentUserIsOwnerSelector, action.content.owner);
    const isOwner: boolean = yield select(ownerSelector);
    const result = yield call(openRenameDialog, action.content, isOwner);

    if (result && result.errors && result.errors.length > 0) {
      const localizedErrorMessage = yield call(getLocalizedErrorMessage, action.content);
      yield put(requestFailed(result.errors[0].message, localizedErrorMessage));
    } else {
      const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.content]);
      if (!maybeReloaded) {
        yield put(requestSpaceContents());
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getLocalizedErrorMessage(content: IContentInfo) {
  if (content.contentType === ProjectDefinition.contentType) {
    return Messages.projectRenameError({ PROJECT_NAME: content.name });
  } else if (content.contentType === WorkbookDefinition.contentType) {
    return Messages.workbookRenameError({ WORKBOOK_NAME: content.name });
  } else if (content.contentType === FlowDefinition.contentType) {
    return Messages.flowRenameError({ FLOW_NAME: content.name });
  } else if (content.contentType === ScheduleDefinition.itemType) {
    return Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(content.name) });
  } else if (content.contentType === MetricDefinition.itemType) {
    return Messages.metricRenameError({ METRIC_NAME: content.name });
  } else if (content.contentType === GroupDefinition.contentType) {
    return Messages.groupRenameError({ GROUP_NAME: content.name });
  } else if (content.contentType === PublishedConnectionDefinition.contentType) {
    return Messages.publishedConnectionRenameError({ PUBLISHED_CONNECTION_NAME: content.name });
  } else if (content.contentType === CollectionDefinition.contentType) {
    return Messages.collectionRenameError({ COLLECTION_NAME: content.name });
  }
  return Messages.errorUnknown();
}

export function openRenameDialog(content: IContentInfo, isOwner: boolean): IPromise<IResult> {
  if (content.contentType === ProjectDefinition.contentType) {
    return RenameAction.renameProject(content);
  } else if (content.contentType === WorkbookDefinition.contentType) {
    return RenameAction.renameWorkbook(content);
  } else if (content.contentType === FlowDefinition.contentType) {
    return RenameAction.renameFlow(content as IContentInfo<'flow'>);
  } else if (content.contentType === ScheduleDefinition.itemType) {
    return RenameAction.renameSchedule(content);
  } else if (content.contentType === MetricDefinition.itemType) {
    return RenameAction.renameMetric(content);
  } else if (content.contentType === GroupDefinition.contentType) {
    return RenameAction.renameGroup(content);
  } else if (content.contentType === PublishedConnectionDefinition.contentType) {
    return RenameAction.renamePublishedConnection(content);
  } else if (content.contentType === CollectionDefinition.contentType) {
    return RenameAction.renameCollection(content, isOwner);
  }
}
